import React, {
  useMemo,
  useRef,
  useLayoutEffect,
  useState,
  useEffect
} from "react";
import { FixedSizeList as List } from "react-window";
import { useNavigate } from "react-router-dom";
import colleges from "../data/colleges.json";
import FilterModal from "./FilterModal";
import "./CollegeSearchModal.css";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Typography,
  Box
} from "@mui/material";
import { useCompare } from "../context/CompareContext";
import CloseIcon from "@mui/icons-material/Close";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
const ALL_BRANCHES = [
  "Computer Science",
  "Electrical Engg",
  "Electronics & Communication",
  "Civil Engg",
  "Information Technology",
  "Mechanical Engg",
  "Chemical Engg",
  "Others",
];

function mapCourseToBranch(courseName = "") {
  const n = courseName.toLowerCase();
  if (n.includes("computer")) return "Computer Science";
  if (n.includes("information")) return "Information Technology";
  if (n.includes("electronics") || n.includes("telecommunication"))
    return "Electronics & Communication";
  if (n.includes("civil")) return "Civil Engg";
  if (n.includes("mechanical")) return "Mechanical Engg";
  if (n.includes("electrical")) return "Electrical Engg";
  if (n.includes("chemical")) return "Chemical Engg";
  return "Others";
}

function branchesForCollege(c) {
  const list = Array.isArray(c?.Courses) ? c.Courses : [];
  return new Set(list.map((x) => mapCourseToBranch(x?.Course_Name || "")));
}

function matchesAutonomy(college, autonomy) {
  if (autonomy === "All") return true;

  const status = String(college.Autonomy_Status || "")
    .trim()
    .toLowerCase();

  if (autonomy === "Autonomous") return status === "autonomous";
  if (autonomy === "Non-Autonomous") return status === "non-autonomous";

  return true;
}

export default function CollegeSearchModal({ isOpen, onClose }) {
  const {
  addCollege,
  setCompareMode,
  removeCollege,
  colleges: compareColleges,
  compareMode
} = useCompare();


  const [searchTerm, setSearchTerm] = useState("");
  const [filterModalOpen, setFilterModalOpen] = useState(false);
  const [videoOpen, setVideoOpen] = useState(false);

  const navigate = useNavigate();

  /* ✅ HIDE FLOATING BUTTON WHILE MODAL OPEN */
 useEffect(() => {
    if (isOpen) {
    setSearchTerm("");
  }
  if (isOpen) {
    document.body.style.overflow = "hidden";
  } else {
    document.body.style.overflow = "auto";
  }

  return () => {
    document.body.style.overflow = "auto";
  };
}, [isOpen]);

  const allDivisions = useMemo(() => {
    const uniq = new Set();
    colleges.forEach((c) => {
      if (c?.Region) uniq.add(String(c.Region));
    });
    return Array.from(uniq);
  }, []);

  const [filters, setFilters] = useState({
    autonomy: "All",
    branches: [],
    divisions: []
  });

  const headerRef = useRef(null);
  const [listHeight, setListHeight] = useState(0);

  useLayoutEffect(() => {
    const compute = () => {
      const headerH = headerRef.current?.offsetHeight || 0;
      setListHeight(window.innerHeight - headerH);
    };

    compute();
    window.addEventListener("resize", compute);
    return () => window.removeEventListener("resize", compute);
  }, []);

  const isAllDivisionsSelected = (selectedDivs) =>
    !selectedDivs ||
    selectedDivs.length === 0 ||
    selectedDivs.length === allDivisions.length;

  /* ✅ SEARCH FILTER */
  const filteredColleges = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();

    return colleges.filter((c) => {
      const name = (c.Institute_Name || "").toLowerCase();
      const district = (c.District || "").toLowerCase();
      const code = String(c.Institute_Code || "").toLowerCase();

      const matchesSearch =
        !q ||
        name.includes(q) ||
        district.includes(q) ||
        code.includes(q);

      if (!matchesSearch) return false;
      if (!matchesAutonomy(c, filters.autonomy)) return false;

      if (!isAllDivisionsSelected(filters.divisions)) {
        const region = String(c.Region || "");
        if (!filters.divisions.includes(region)) return false;
      }

      if (filters.branches.length > 0) {
        const collegeBranches = branchesForCollege(c);
        const hasAny = filters.branches.some((b) =>
          collegeBranches.has(b)
        );
        if (!hasAny) return false;
      }

      return true;
    });
  }, [searchTerm, filters, allDivisions]);

  if (!isOpen) return null;

  /* ✅ Virtualized Row */
  const Row = ({ index, style }) => {
    const college = filteredColleges[index];

    return (
      <div
        style={{ ...style, cursor: "pointer" }}
        className="college-row"
        onClick={() => {

  // If user is picking 2nd college for compare
  if (compareMode) {

    addCollege({
      id: college.Institute_Code,
      name: college.Institute_Name,
      university: college.University,
      autonomyStatus: college.Autonomy_Status,
      district: college.District,
      fee: college.Fee,
      intake: college.Total_Intake,
      webAddress: college.Web_Address,
    });

    onClose(); // close modal
    return;    // stop normal navigation
  }

  // Normal flow
  onClose();
  navigate(`/college/${college.Institute_Code}`);

}}

      >
        <div className="college-info">
          <div className="college-name">
            {college.Institute_Name}
          </div>
          <div className="college-location">
            {college.District}
          </div>
        </div>

        <div className="college-code">
          {college.Institute_Code}
        </div>
      </div>
    );
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content">
        <div ref={headerRef} className="modal-header">
            {/* 🔙 Back Button */}
  <button className="back-btn" onClick={onClose}>
    <ArrowBackIcon />
  </button>
          <input
            type="text"
            placeholder="Search by College Name / District / Choice Code"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
            autoFocus
          />

          <button
            className="filter-btn"
            onClick={() => setFilterModalOpen(true)}
          >
            <svg viewBox="0 0 24 24" width="20" height="20">
              <path
                d="M4 7h16M7 12h10M10 17h4"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                fill="none"
              />
            </svg>
          </button>
          <button
  className="info-btn"
  onClick={() => setVideoOpen(true)}
  title="Watch Tutorial"
>
  <InfoOutlinedIcon fontSize="small" />
</button>



        </div>

        <div className="college-list-container">
          <List
            height={listHeight}
            itemCount={filteredColleges.length}
            itemSize={72}
            width="100%"
          >
            {Row}
          </List>
        </div>
      </div>

      {filterModalOpen && (
        <div className="filter-modal-overlay">
          <div className="filter-modal-content">
            <FilterModal
              isOpen={filterModalOpen}
              onClose={() => setFilterModalOpen(false)}
              onApply={(newFilters) => setFilters(newFilters)}
              filters={filters}
              allBranches={ALL_BRANCHES}
              allDivisions={allDivisions}
            />
          </div>
        </div>
      )}
      {/* Tutorial Video Dialog */}
<Dialog
  open={videoOpen}
  onClose={() => setVideoOpen(false)}
  maxWidth="md"
  fullWidth
>
  <DialogTitle
  sx={{
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    py: { xs: 0.5, sm: 1.2 },   // 🔽 reduce vertical height
    px: 2
  }}
  >
    <Typography
  sx={{
    fontSize: { xs: "16px", sm: "20px" },
    fontWeight: 600
  }}
>
  Watch Tutorial
</Typography>

    <IconButton onClick={() => setVideoOpen(false)}>
      <CloseIcon />
    </IconButton>
  </DialogTitle>

  <DialogContent dividers sx={{ p: 0 }}>
    <Box
      sx={{
        position: "relative",
        width: "100%",
        paddingTop: "56.25%",
        backgroundColor: "#000"
      }}
    >
      <iframe
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          border: 0
        }}
        src="https://player.vimeo.com/video/1166015267?badge=0&amp;autopause=0&amp;player_id=0&amp;app_id=58479"
        title="Tutorial Video"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
        allowFullScreen
      />
    </Box>
  </DialogContent>
</Dialog>

    </div>
  );
}
